import React, { useState, useRef } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import productImgPink from "../assets/productImg.png";
import productImgBlack from "../assets/productImgBlack.png";
import b1 from "../assets/b1.webp";
import b2 from "../assets/b2.webp";
import b3 from "../assets/b3.jpg";
import b4 from "../assets/b4.jpg";
import b5 from "../assets/b5.webp";
import b6 from "../assets/b6.avif";
import p1 from "../assets/p1.png";
import p2 from "../assets/p2.webp";
import p3 from "../assets/p3.jpg";
import p4 from "../assets/p4.webp";
import p5 from "../assets/p5.avif";
import p6 from "../assets/p6.webp";

const EMAILJS_SERVICE_ID = "service_azupjfp";
const EMAILJS_TEMPLATE_ID = "template_4uwpfg9";
const EMAILJS_PUBLIC_KEY = "3XrECIpzbXtoB-q3F";

const SHIPPING = { Kosovo: 2, Albania: 5, Macedonia: 5 };
const PRODUCT_PRICE = 19.99;

const galleryByColor = {
  "Blush Pink": [productImgPink, p3, p2, p1, p4, p5, p6],
  "Midnight Black": [productImgBlack, b1, b2, b3, b4, b5, b6],
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stepRef = useRef(null);

  const initialColor = location.state?.color || "Blush Pink";

  const [step, setStep] = useState(1);
  const [color, setColor] = useState(initialColor);
  const [quantity, setQuantity] = useState(1);
  const [mainImg, setMainImg] = useState(0);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "Kosovo",
    address: "",
    city: "",
    zip: "",
  });
  const [loading, setLoading] = useState(false);
  const [formErr, setFormErr] = useState({});

  const gallery = galleryByColor[color];
  const shipping = SHIPPING[form.country];
  const total = (PRODUCT_PRICE * quantity + shipping).toFixed(2);

  const handleColorChange = (c) => {
    setColor(c);
    setMainImg(0);
  };

  const goToStep2 = () => {
    gsap.fromTo(
      stepRef.current,
      { opacity: 0, x: 40 },
      { opacity: 1, x: 0, duration: 0.45, ease: "power2.out" },
    );
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToStep1 = () => {
    gsap.fromTo(
      stepRef.current,
      { opacity: 0, x: -40 },
      { opacity: 1, x: 0, duration: 0.45, ease: "power2.out" },
    );
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormErr({ ...formErr, [e.target.name]: "" });
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Emri është i detyrueshëm";
    if (!form.phone.trim()) err.phone = "Numri është i detyrueshëm";
    if (!form.address.trim()) err.address = "Adresa është e detyrueshme";
    if (!form.city.trim()) err.city = "Qyteti është i detyrueshëm";
    return err;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (Object.keys(err).length) {
      setFormErr(err);
      return;
    }
    setLoading(true);
    try {
      const emailjs = await import("@emailjs/browser");
      const templateParams = {
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        customer_country: form.country,
        customer_address: `${form.address}, ${form.city}${form.zip ? ", " + form.zip : ""}`,
        product_name: "Siara Styler",
        product_color: color,
        product_quantity: quantity,
        product_price: `€${PRODUCT_PRICE}`,
        shipping_price: `€${shipping}`,
        order_total: `€${total}`,
      };
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY,
      );
      navigate("/thank-you", { state: { name: form.name } });
    } catch (err) {
      console.error("EmailJS error:", err);
      alert(
        "Diçka shkoi keq. Ju lutemi provoni përsëri ose na kontaktoni direkt.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <TopBar>
        <Logo onClick={() => navigate("/")}>SIARA</Logo>
        <Steps>
          <StepDot $active={step === 1} $done={step > 1}>
            1
          </StepDot>
          <StepLine $done={step > 1} />
          <StepDot $active={step === 2} $done={false}>
            2
          </StepDot>
        </Steps>
        <StepLabel>{step === 1 ? "Produkti" : "Detajet"}</StepLabel>
      </TopBar>

      <Content ref={stepRef}>
        {step === 1 ? (
          <TwoCol>
            <GalleryCol>
              <MainImage>
                <img src={gallery[mainImg]} alt={color} draggable="false" />
              </MainImage>
              <Thumbnails>
                {gallery.map((img, i) => (
                  <Thumb
                    key={i}
                    $active={mainImg === i}
                    onClick={() => setMainImg(i)}
                  >
                    <img src={img} alt={`view-${i}`} draggable="false" />
                  </Thumb>
                ))}
              </Thumbnails>
            </GalleryCol>

            <DetailCol>
              <ProductTag>Siara Beauty</ProductTag>
              <ProductName>Siara Styler</ProductName>
              <ProductPrice>€{PRODUCT_PRICE}</ProductPrice>

              <Divider />

              <OptionLabel>
                Ngjyra — <strong>{color}</strong>
              </OptionLabel>
              <ColorOptions>
                {["Blush Pink", "Midnight Black"].map((c) => (
                  <ColorBtn
                    key={c}
                    $active={color === c}
                    onClick={() => handleColorChange(c)}
                  >
                    <ColorSwatch $black={c === "Midnight Black"} />
                    {c}
                  </ColorBtn>
                ))}
              </ColorOptions>

              <OptionLabel>Sasia</OptionLabel>
              <QuantityRow>
                <QBtn onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                  −
                </QBtn>
                <QVal>{quantity}</QVal>
                <QBtn onClick={() => setQuantity((q) => q + 1)}>+</QBtn>
              </QuantityRow>

              <Divider />

              <FeatureList>
                <Feature>⚡ USB-C mbushës i përfshirë</Feature>
                <Feature>🪶 180g — sper i lehtë</Feature>
                <Feature>🔥 200°C — ngrohje e sigurt pa djegje</Feature>
                <Feature>📦 8×8×3 cm — vendoset kudo</Feature>
                <Feature>🚚 Transport: Kosovë, Shqipëri, Maqedoni</Feature>
                {/* <Feature>↩️ 30 ditë garanci kthimi</Feature> */}
              </FeatureList>

              <Divider />

              <ProductDescription>
                Siara Styler është drejtuesi i parë portativ i flokëve i
                disponueshëm në Kosovë. I vogël, i fuqishëm dhe i sigurt —
                karikoje me USB-C dhe ke flokë të drejtë kudo që të jesh. Pa
                djegie, pa dëmtime.
              </ProductDescription>

              <NextBtn onClick={goToStep2}>Vazhdo me porosinë →</NextBtn>
            </DetailCol>
          </TwoCol>
        ) : (
          <TwoCol>
            <OrderSummaryCol>
              <SummaryTitle>Përmbledhja e porosisë</SummaryTitle>
              <SummaryProduct>
                <SummaryImg src={gallery[0]} alt={color} />
                <SummaryInfo>
                  <SummaryName>Siara Styler</SummaryName>
                  <SummaryMeta>
                    {color} · Sasia: {quantity}
                  </SummaryMeta>
                  <SummaryPrice>
                    €{(PRODUCT_PRICE * quantity).toFixed(2)}
                  </SummaryPrice>
                </SummaryInfo>
              </SummaryProduct>

              <SummaryLines>
                <SummaryLine>
                  <span>Produkti ({quantity}×)</span>
                  <span>€{(PRODUCT_PRICE * quantity).toFixed(2)}</span>
                </SummaryLine>
                <SummaryLine>
                  <span>Transport ({form.country})</span>
                  <span>€{shipping}.00</span>
                </SummaryLine>
                <SummaryTotal>
                  <span>Totali</span>
                  <span>€{total}</span>
                </SummaryTotal>
              </SummaryLines>

              <BackBtn onClick={goToStep1}>← Kthehu</BackBtn>
            </OrderSummaryCol>

            <FormCol>
              <FormTitle>Informacionet e dërgesës</FormTitle>

              <FormRow>
                <Field>
                  <Label>Emri i plotë *</Label>
                  <Input
                    name="name"
                    placeholder="Emri Mbiemri"
                    value={form.name}
                    onChange={handleChange}
                    $err={!!formErr.name}
                  />
                  {formErr.name && <ErrMsg>{formErr.name}</ErrMsg>}
                </Field>
              </FormRow>

              <FormRow>
                <Field>
                  <Label>Numri i telefonit *</Label>
                  <Input
                    name="phone"
                    placeholder="+383 44 000 000"
                    value={form.phone}
                    onChange={handleChange}
                    $err={!!formErr.phone}
                  />
                  {formErr.phone && <ErrMsg>{formErr.phone}</ErrMsg>}
                </Field>
                <Field>
                  <Label>Email (opsionale)</Label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="emri@email.com"
                    value={form.email}
                    onChange={handleChange}
                    $err={!!formErr.email}
                  />
                  {formErr.email && <ErrMsg>{formErr.email}</ErrMsg>}
                </Field>
              </FormRow>

              <FormRow>
                <Field>
                  <Label>Shteti *</Label>
                  <Select
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                  >
                    <option value="Kosovo">Kosovë — transport €2</option>
                    <option value="Albania">Shqipëri — transport €5</option>
                    <option value="Macedonia">Maqedoni — transport €5</option>
                  </Select>
                </Field>
                <Field>
                  <Label>Qyteti *</Label>
                  <Input
                    name="city"
                    placeholder="Prishtinë"
                    value={form.city}
                    onChange={handleChange}
                    $err={!!formErr.city}
                  />
                  {formErr.city && <ErrMsg>{formErr.city}</ErrMsg>}
                </Field>
              </FormRow>

              <FormRow>
                <Field style={{ flex: 2 }}>
                  <Label>Adresa *</Label>
                  <Input
                    name="address"
                    placeholder="Rruga, nr. shtëpisë / apartamentit"
                    value={form.address}
                    onChange={handleChange}
                    $err={!!formErr.address}
                  />
                  {formErr.address && <ErrMsg>{formErr.address}</ErrMsg>}
                </Field>
              </FormRow>

              <SubmitBtn onClick={handleSubmit} disabled={loading}>
                {loading ? "Duke dërguar..." : `Porosit Tani · €${total}`}
              </SubmitBtn>
              <FormNote>
                Duke klikuar "Porosit Tani" ju pranoni kushtet tona. Pagesa
                bëhet me dorëzim (cash on delivery).
              </FormNote>
            </FormCol>
          </TwoCol>
        )}
      </Content>
    </Page>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: var(--cream);
  font-family: var(--sans);
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 3rem;
  border-bottom: 1px solid var(--blush);
  background: rgba(253, 248, 245, 0.95);
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: 50;

  @media (max-width: 768px) {
    padding: 0.9rem 1.2rem;
  }
`;

const Logo = styled.span`
  font-family: var(--serif);
  font-size: 1.3rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  color: var(--dark);
  cursor: pointer;
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
`;

const StepDot = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 500;
  background: ${({ $active, $done }) =>
    $done ? "var(--gold)" : $active ? "var(--dark)" : "transparent"};
  color: ${({ $active, $done }) =>
    $done || $active ? "var(--cream)" : "var(--mid)"};
  border: 1px solid
    ${({ $active, $done }) =>
      $done ? "var(--gold)" : $active ? "var(--dark)" : "var(--rose)"};
  transition: all 0.3s ease;
`;

const StepLine = styled.div`
  width: 40px;
  height: 1px;
  background: ${({ $done }) => ($done ? "var(--gold)" : "var(--rose)")};
  transition: background 0.3s ease;
`;

const StepLabel = styled.span`
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--mid);

  @media (max-width: 400px) {
    display: none;
  }
`;

const Content = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2.5rem 3rem;

  @media (max-width: 1024px) {
    padding: 2rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1.2rem;
  }
`;

const TwoCol = styled.div`
  display: flex;
  gap: 4rem;
  align-items: flex-start;

  @media (max-width: 1024px) {
    gap: 2.5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

// ── Step 1 ──

const GalleryCol = styled.div`
  flex: 1;
  position: sticky;
  top: 5rem;

  @media (max-width: 768px) {
    position: static;
    width: 100%;
  }
`;

const MainImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: linear-gradient(135deg, #fdf8f5, #fceee8);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.8rem;
  overflow: hidden;

  img {
    width: 75%;
    object-fit: contain;
    filter: drop-shadow(0 20px 40px rgba(180, 120, 100, 0.2));
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    aspect-ratio: 4/3;

    img {
      width: 60%;
    }
  }
`;

const Thumbnails = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.4rem;
  }
`;

const Thumb = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fdf8f5, #fceee8);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1.5px solid
    ${({ $active }) => ($active ? "var(--dusty)" : "transparent")};
  transition: border-color 0.2s ease;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 80%;
    object-fit: contain;
  }
  &:hover {
    border-color: var(--rose);
  }

  @media (max-width: 768px) {
    width: 58px;
    height: 58px;
    border-radius: 8px;
  }

  @media (max-width: 400px) {
    width: 50px;
    height: 50px;
  }
`;

const DetailCol = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
    padding-top: 1.5rem;
  }
`;

const ProductTag = styled.p`
  font-size: 0.68rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 0.4rem;
`;

const ProductName = styled.h1`
  font-family: var(--serif);
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 300;
  color: var(--dark);
  margin-bottom: 0.4rem;
  line-height: 1.1;
`;

const ProductPrice = styled.div`
  font-family: var(--serif);
  font-size: 1.8rem;
  font-weight: 400;
  color: var(--dark);
  margin-bottom: 1.2rem;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--blush);
  margin: 1rem 0;
`;

const OptionLabel = styled.p`
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--mid);
  margin-bottom: 0.6rem;

  strong {
    color: var(--dark);
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
  }
`;

const ColorOptions = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 1.2rem;
  flex-wrap: wrap;
`;

const ColorBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--sans);
  font-size: 0.78rem;
  font-weight: ${({ $active }) => ($active ? "500" : "300")};
  color: ${({ $active }) => ($active ? "var(--dark)" : "var(--mid)")};
  background: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.8)" : "transparent"};
  border: 1px solid
    ${({ $active }) => ($active ? "var(--dusty)" : "var(--rose)")};
  border-radius: 100px;
  padding: 0.5rem 1rem 0.5rem 0.6rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--dusty);
    color: var(--dark);
  }
`;

const ColorSwatch = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ $black }) => ($black ? "#1a1a1a" : "#f2d0c8")};
  border: 1px solid
    ${({ $black }) => ($black ? "transparent" : "rgba(180,120,100,0.2)")};
  flex-shrink: 0;
`;

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.2rem;
  width: fit-content;
  border: 1px solid var(--rose);
  border-radius: 100px;
  overflow: hidden;
`;

const QBtn = styled.button`
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  font-size: 1.1rem;
  color: var(--dark);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--blush);
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`;

const QVal = styled.span`
  width: 44px;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--dark);
  border-left: 1px solid var(--rose);
  border-right: 1px solid var(--rose);
  line-height: 44px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-bottom: 0.4rem;
`;

const Feature = styled.p`
  font-size: 0.85rem;
  font-weight: 300;
  color: var(--mid);
  line-height: 1.5;
`;

const ProductDescription = styled.p`
  font-size: 0.88rem;
  font-weight: 300;
  line-height: 1.75;
  color: var(--mid);
  margin-bottom: 1.6rem;
`;

const NextBtn = styled.button`
  width: 100%;
  font-family: var(--sans);
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--cream);
  background: var(--dark);
  border: none;
  padding: 1.1rem 2rem;
  border-radius: 100px;
  cursor: pointer;
  transition:
    background 0.3s ease,
    transform 0.2s ease;

  &:hover {
    background: var(--gold);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 1.2rem 2rem;
    font-size: 0.85rem;
  }
`;

// ── Step 2 ──

const OrderSummaryCol = styled.div`
  flex: 1;
  position: sticky;
  top: 5rem;

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    /* Show as a compact bar on mobile */
    background: linear-gradient(135deg, #fdf8f5, #fceee8);
    border-radius: 16px;
    padding: 1.2rem;
    margin-bottom: 1.5rem;
  }
`;

const SummaryTitle = styled.h2`
  font-family: var(--serif);
  font-size: 1.6rem;
  font-weight: 300;
  color: var(--dark);
  margin-bottom: 1.2rem;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0.8rem;
  }
`;

const SummaryProduct = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  background: linear-gradient(135deg, #fdf8f5, #fceee8);
  border-radius: 14px;
  padding: 1rem;
  margin-bottom: 1.2rem;

  @media (max-width: 768px) {
    background: rgba(255, 255, 255, 0.5);
    padding: 0.8rem;
    border-radius: 10px;
    margin-bottom: 0.8rem;
  }
`;

const SummaryImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 8px 16px rgba(180, 120, 100, 0.18));

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const SummaryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const SummaryName = styled.p`
  font-family: var(--serif);
  font-size: 1.1rem;
  font-weight: 400;
  color: var(--dark);
`;

const SummaryMeta = styled.p`
  font-size: 0.78rem;
  font-weight: 300;
  color: var(--mid);
`;

const SummaryPrice = styled.p`
  font-family: var(--serif);
  font-size: 1.1rem;
  color: var(--dark);
`;

const SummaryLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-top: 1px solid var(--blush);
  padding-top: 0.8rem;
  margin-bottom: 1.4rem;
`;

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  font-weight: 300;
  color: var(--mid);
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: var(--serif);
  font-size: 1.2rem;
  font-weight: 400;
  color: var(--dark);
  border-top: 1px solid var(--blush);
  padding-top: 0.7rem;
  margin-top: 0.3rem;
`;

const BackBtn = styled.button`
  font-family: var(--sans);
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--mid);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    color: var(--dark);
  }
`;

const FormCol = styled.div`
  flex: 1.2;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormTitle = styled.h2`
  font-family: var(--serif);
  font-size: 1.6rem;
  font-weight: 300;
  color: var(--dark);
  margin-bottom: 1.4rem;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.8rem;
  }
`;

const Field = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.7rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--mid);
`;

const Input = styled.input`
  font-family: var(--sans);
  font-size: 0.9rem;
  font-weight: 300;
  color: var(--dark);
  background: white;
  border: 1px solid ${({ $err }) => ($err ? "#c0392b" : "var(--rose)")};
  border-radius: 10px;
  padding: 0.75rem 1rem;
  outline: none;
  transition: border-color 0.2s ease;
  /* Prevent zoom on iOS */
  font-size: 16px;

  &:focus {
    border-color: var(--dusty);
  }
  &::placeholder {
    color: rgba(90, 62, 53, 0.35);
  }

  @media (min-width: 769px) {
    font-size: 0.9rem;
  }
`;

const Select = styled.select`
  font-family: var(--sans);
  font-weight: 300;
  color: var(--dark);
  background: white;
  border: 1px solid var(--rose);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s ease;
  /* Prevent zoom on iOS */
  font-size: 16px;

  &:focus {
    border-color: var(--dusty);
  }

  @media (min-width: 769px) {
    font-size: 0.9rem;
  }
`;

const ErrMsg = styled.span`
  font-size: 0.7rem;
  color: #c0392b;
`;

const SubmitBtn = styled.button`
  width: 100%;
  font-family: var(--sans);
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--cream);
  background: ${({ disabled }) => (disabled ? "var(--dusty)" : "var(--dark)")};
  border: none;
  padding: 1.2rem 2rem;
  border-radius: 100px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  margin-top: 1.2rem;
  margin-bottom: 0.8rem;
  transition:
    background 0.3s ease,
    transform 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--gold);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 1.3rem 2rem;
    font-size: 0.88rem;
    /* Sticky at bottom on mobile */
    position: sticky;
    bottom: 1rem;
    box-shadow: 0 8px 32px rgba(42, 31, 26, 0.2);
  }
`;

const FormNote = styled.p`
  font-size: 0.72rem;
  font-weight: 300;
  color: var(--dusty);
  line-height: 1.6;
  text-align: center;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

export default Checkout;
